import React from 'react';
import { Supplier, SupplierBranch } from '@dill/dill-shared';
import { ShieldCheck, Headset, ClockClockwise, IdentificationCard } from 'phosphor-react';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';

const BADGE_ICON_STYLE = { background: 'linear-gradient(135deg, #3762FB 0%, #1D85FF 100%)' };

const TrustBadge = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="flex items-start gap-3 p-3 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER">
    <div
      className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full"
      style={BADGE_ICON_STYLE}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-semibold GREY_900-CLR">{title}</p>
      <p className="text-xs GREY_500-CLR mt-0.5">{description}</p>
    </div>
  </div>
);

const SupportCard = () => (
  <div className="p-4 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER">
    <div className="flex items-center gap-2 mb-3">
      <div
        className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full"
        style={BADGE_ICON_STYLE}>
        <Headset size={18} className="text-white" weight="fill" />
      </div>
      <p className="text-sm font-semibold GREY_900-CLR">Need Help?</p>
    </div>
    <div className="space-y-1">
      <a
        href="mailto:support@usedill.com"
        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
        <span className="GREY_500-CLR">Email:</span>
        <span className="PRIMARY_500-CLR font-medium">support@usedill.com</span>
      </a>
      <a
        href="tel:6283040931"
        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
        <span className="GREY_500-CLR">Phone:</span>
        <span className="PRIMARY_500-CLR font-medium">(628) 304-0931</span>
      </a>
    </div>
  </div>
);

const SidebarSkeleton = () => (
  <div className="animate-pulse flex flex-col gap-4">
    <div className="w-full h-24 rounded-2xl GREY_200-BG" />
    <div className="flex flex-col items-center gap-2">
      <div className="h-4 w-32 rounded GREY_200-BG" />
      <div className="h-3 w-24 rounded GREY_200-BG" />
      <div className="h-3 w-20 rounded GREY_200-BG" />
    </div>
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="flex items-start gap-3 p-3 rounded-xl GREY_100-BG border GREY_100-BORDER">
        <div className="w-9 h-9 rounded-full GREY_200-BG flex-shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-3 w-16 rounded GREY_200-BG" />
          <div className="h-2.5 w-full rounded GREY_200-BG" />
        </div>
      </div>
    ))}
    <div className="p-4 rounded-xl GREY_100-BG border GREY_100-BORDER flex flex-col gap-2">
      <div className="h-3 w-20 rounded GREY_200-BG" />
      <div className="h-2.5 w-36 rounded GREY_200-BG" />
      <div className="h-2.5 w-28 rounded GREY_200-BG" />
    </div>
  </div>
);

// The supplier card, trust badges and support contact down the left of the public pages reached
// from emailed links (internal signature documents, internal forms).
const PublicFormSidebar = ({
  supplier,
  isLoading = false,
}: {
  supplier: (Supplier & { branches?: SupplierBranch[] }) | null;
  isLoading?: boolean;
}) => {
  const address = `${supplier?.address ? `${supplier?.address},` : ''}`;
  const cityStateZip = `${supplier?.city ? `${supplier?.city},` : ''} ${supplier?.state ?? ''} ${
    supplier?.zipCode ?? ''
  }`;
  return (
    <div className="flex flex-col px-5 py-8 w-full md:w-[260px] GREY_50-BG border-b md:border-b-0 md:border-r GREY_200-BORDER md:h-screen md:sticky md:top-0 md:overflow-y-auto">
      {isLoading ? (
        <SidebarSkeleton />
      ) : (
        <>
          <div className="flex flex-col items-center gap-3 mb-6">
            {supplier?.logo?.url ? (
              <div className="flex items-center justify-center w-full h-24 p-3 rounded-2xl WHITE-BG shadow-sm border GREY_100-BORDER">
                <img
                  src={supplier.logo.url}
                  className="max-h-16 max-w-full object-contain"
                  alt={supplier?.name ?? ''}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-20 p-4 rounded-2xl WHITE-BG shadow-sm border GREY_100-BORDER">
                <img className="w-7/12 object-contain" src={dillLogoWithText} alt="Dill" />
              </div>
            )}
            <div className="text-center">
              <p className="text-base font-bold GREY_900-CLR">{supplier?.name ?? ''}</p>
              {address && <p className="text-xs GREY_500-CLR mt-0.5">{address}</p>}
              {cityStateZip.trim() && <p className="text-xs GREY_500-CLR">{cityStateZip}</p>}
              <div className="flex flex-col items-center mt-0.5">
                {(supplier?.creditApplicationContactEmail?.length
                  ? supplier.creditApplicationContactEmail.join(', ')
                  : supplier?.email) && (
                  <a
                    href={`mailto:${
                      supplier?.creditApplicationContactEmail?.[0] ?? supplier?.email
                    }`}
                    className="text-xs PRIMARY_500-CLR hover:opacity-80 transition-opacity">
                    {supplier?.creditApplicationContactEmail?.join(', ') ?? supplier?.email}
                  </a>
                )}
                {(supplier?.creditApplicationContactPhoneNumber?.length
                  ? supplier.creditApplicationContactPhoneNumber.join(', ')
                  : supplier?.phoneNumber) && (
                  <a
                    href={`tel:${
                      supplier?.creditApplicationContactPhoneNumber?.[0] ?? supplier?.phoneNumber
                    }`}
                    className="text-xs PRIMARY_500-CLR hover:opacity-80 transition-opacity">
                    {supplier?.creditApplicationContactPhoneNumber?.join(', ') ??
                      supplier?.phoneNumber}
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 mb-6 h-full justify-between mt-12">
            <div className="flex flex-col gap-3 mb-6">
              <TrustBadge
                icon={<ShieldCheck size={18} className="text-white" weight="fill" />}
                title="Secure"
                description="Your data is fully encrypted and protected."
              />
              <TrustBadge
                icon={<ClockClockwise size={18} className="text-white" weight="fill" />}
                title="Quick & Easy"
                description="Complete your application in minutes."
              />
              <TrustBadge
                icon={<IdentificationCard size={18} className="text-white" weight="fill" />}
                title="No Account Required"
                description="Apply without creating an account."
              />
            </div>

            <SupportCard />
          </div>
        </>
      )}
    </div>
  );
};

export default PublicFormSidebar;
